import React, { useState, useContext, useEffect } from 'react';

import {
  Accordion,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';

import { useHistory } from 'react-router-dom';

import UserRoleContext from 'context/user-role/context';
import { isAllowedByOffice } from 'context/user-role/helpers';

import { useDevice, useGlobalApp } from 'hooks';

import type { ItemSidebarMenu } from 'commons/modules/SidebarMenu/item-sidebar-menu';

import { hoverStyle } from '../style';

import ItemHome from './ItemHome';

import { getItemsMenuByAccess } from './helpers';

import { styles, AccordionSummary } from './style';

interface Props {
  open: boolean;
  returnPath: (path: string) => boolean;
  role: string;
  closeDrawer: () => void;
}

const Items = ({ open, role, closeDrawer, returnPath }: Props) => {
  const history = useHistory();
  const { accessByMenu, isSuperAdmin } = useContext(UserRoleContext);
  const { investmentOfficeId } = useGlobalApp();
  // 👇 usar hasHover en lugar de isMobile para eventos de mouse
  const { isMobile, hasHover } = useDevice();

  const [expanded, setExpanded] = useState<string | null | false>(false);
  const [isHover, setIsHover] = useState(false);
  const [currentImage, setCurrentImage] = useState('');
  const [items, setItems] = useState<ItemSidebarMenu[]>([]);

  const onChangeHover = (
    e: React.MouseEvent<HTMLLIElement, MouseEvent>,
    text: string,
    menuKey: string | undefined,
  ) => {
    setIsHover(true);
    setCurrentImage(menuKey || '');
  };

  const onCleanHover = () => {
    setIsHover(false);
    setCurrentImage('');
  };

  const handleMouseEnter = (
    e: React.MouseEvent<HTMLLIElement, MouseEvent>,
    i: ItemSidebarMenu,
  ) => {
    if (!hasHover) return;
    onChangeHover(e, i.title, i.menuKey);
  };

  const handleAccordionClick = (menuKey: string) => {
    if (isMobile) {
      setExpanded(expanded === `panel${menuKey}` ? null : `panel${menuKey}`);
    }
  };

  const navigate = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    newEvent: any,
  ) => {
    history.push(`${newEvent.nav}`);
    closeDrawer();
    if (isMobile) {
      setExpanded(null);
      onCleanHover();
    }
  };

  useEffect(() => {
    setItems(getItemsMenuByAccess(accessByMenu));
  }, [accessByMenu]);

  return (
    <>
      <ItemHome
        open={open}
        expanded={expanded}
        currentImage={currentImage}
        onCleanHover={onCleanHover}
        onChangeHover={onChangeHover}
        returnPath={returnPath}
        closeDrawer={closeDrawer}
      />
      {items
        .filter(
          (menuItem) =>
            menuItem.access[role] &&
            isAllowedByOffice(menuItem.exclusiveByOffice, investmentOfficeId),
        )
        .map((i: ItemSidebarMenu, index) => {
          const access = i.access[role] || isSuperAdmin;
          return (
            <ListItem
              sx={styles.listItem}
              onMouseEnter={(e) => handleMouseEnter(e, i)}
              // 👇 hasHover en lugar de isMobile para eventos de mouse
              onMouseLeave={hasHover ? onCleanHover : undefined}
              onTouchStart={!hasHover ? () => setIsHover(false) : undefined}
              key={index}
            >
              <Accordion
                elevation={0}
                sx={styles.itemMenu}
                expanded={expanded === `panel${i.menuKey}` && open}
                onMouseEnter={
                  hasHover ? () => setExpanded(`panel${i.menuKey}`) : undefined
                }
                onMouseLeave={hasHover ? () => setExpanded(null) : undefined}
                onClick={() => handleAccordionClick(i.menuKey || '')}
              >
                <AccordionSummary style={{ padding: 0 }}>
                  <i.icon
                    open={open}
                    currentImage={currentImage}
                    isExpanded={String(expanded)}
                    disabled={!access}
                    returnPath={returnPath}
                  />
                  {open ? (
                    <Typography
                      sx={[
                        open && expanded === false
                          ? styles.textItemLight
                          : expanded === `panel${i.menuKey}` && open
                            ? styles.textItemExpanded
                            : styles.textItemContacted,
                        ...(isHover && expanded === `panel${i.menuKey}`
                          ? [hoverStyle]
                          : []),
                      ]}
                    >
                      {i.title}
                    </Typography>
                  ) : null}
                </AccordionSummary>
                <AccordionDetails sx={styles.details}>
                  <List>
                    {i.options
                      .filter((opt) => {
                        if (isSuperAdmin) {
                          if (opt.exclusiveByOffice?.length) {
                            return opt.exclusiveByOffice.includes(
                              investmentOfficeId,
                            );
                          }
                          return true;
                        }
                        if (!opt.access?.[role]) return false;
                        return isAllowedByOffice(
                          opt.exclusiveByOffice,
                          investmentOfficeId,
                        );
                      })
                      .map((opt) => {
                        const accessRole = opt.access[role] || isSuperAdmin;
                        return (
                          <ListItem
                            button
                            sx={styles.itemList}
                            onClick={(e) => navigate(e, opt)}
                            key={opt.title}
                            disabled={!accessRole}
                          >
                            <ListItemText
                              sx={styles.textItem}
                              disableTypography
                            >
                              {opt.title}
                            </ListItemText>
                          </ListItem>
                        );
                      })}
                  </List>
                </AccordionDetails>
              </Accordion>
            </ListItem>
          );
        })}
    </>
  );
};

export default React.memo(Items);
